/**
 * A single action step in the playbook builder.
 * Renders: type picker → meta-driven config fields → delay control (steps 2+).
 * Destructive actions show the red blast-radius banner.
 */
import {
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import {
  ACTION_META,
  ACTION_TYPE_OPTIONS,
  defaultConfig,
  type PlaybookActionType,
} from './action-type-meta';
import { EdgeActionWarning } from './EdgeActionWarning';
import { WatchpointMultiselect } from './WatchpointMultiselect';
import { WebhookFields } from './WebhookFields';
import type { PlaybookAction, Channel, Watchpoint } from '@/api/healthcheck';

interface Props {
  index: number;
  action: PlaybookAction;
  totalCount: number;
  channels: Channel[];
  watchpoints: Watchpoint[];
  showErrors: boolean;
  onChange: (action: PlaybookAction) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

const SEVERITY_OPTIONS = ['info', 'warning', 'critical'] as const;

export function ActionRow({
  index,
  action,
  totalCount,
  channels,
  watchpoints,
  showErrors,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: Props) {
  const meta = ACTION_META[action.type as PlaybookActionType];
  const isFirst = index === 0;
  const isLast = index === totalCount - 1;

  const handleTypeChange = (newType: string) => {
    onChange({
      type: newType as PlaybookActionType,
      config: defaultConfig(newType as PlaybookActionType),
      delay: action.delay,
    });
  };

  const handleConfigChange = (fieldName: string, value: unknown) => {
    onChange({ ...action, config: { ...action.config, [fieldName]: value } });
  };

  // Atomic multi-field update. Needed when one action (e.g. applying a webhook
  // template) sets several config keys at once — two separate handleConfigChange
  // calls would each derive from the same stale `action`, so the second clobbers
  // the first.
  const handleConfigPatch = (patch: Record<string, unknown>) => {
    onChange({ ...action, config: { ...action.config, ...patch } });
  };

  const handleDelayChange = (val: string) => {
    const n = Math.max(0, Number(val) || 0);
    onChange({ ...action, delay: n });
  };

  const channelIdMissing =
    showErrors &&
    action.type === 'notify_oncall' &&
    !action.config['channel_id'];

  return (
    <div
      style={{
        border: '1px solid var(--vn-border-default)',
        borderRadius: 'var(--vn-radius-md)',
        padding: 'var(--vn-space-4)',
        background: 'var(--vn-bg-surface)',
        display: 'grid',
        gap: 'var(--vn-space-3)',
      }}
    >
      {/* Header: type picker + reorder + remove */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <span
          style={{
            fontSize: 'var(--vn-font-size-xs)',
            color: 'var(--vn-fg-muted)',
            minWidth: 56,
          }}
        >
          {isFirst ? 'Immediate' : `Step ${index + 1}`}
        </span>

        <Select value={action.type} onValueChange={handleTypeChange}>
          <SelectTrigger style={{ flex: 1, maxWidth: 280 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTION_TYPE_OPTIONS.map((t) => {
              const m = ACTION_META[t];
              const Icon = m.icon;
              return (
                <SelectItem key={t} value={t}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', color: m.destructive ? 'var(--vn-danger-text)' : undefined }}>
                    <Icon size={14} />
                    {m.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <div style={{ display: 'flex', gap: 'var(--vn-space-1)', marginLeft: 'auto' }}>
          <IconButton
            size="sm"
            variant="ghost"
            onClick={onMoveUp}
            disabled={isFirst}
            aria-label="Move up"
          >
            <ChevronUp size={14} />
          </IconButton>
          <IconButton
            size="sm"
            variant="ghost"
            onClick={onMoveDown}
            disabled={isLast}
            aria-label="Move down"
          >
            <ChevronDown size={14} />
          </IconButton>
          <IconButton
            size="sm"
            variant="ghost"
            onClick={onRemove}
            aria-label="Remove action"
            style={{ color: 'var(--vn-danger-text)' }}
          >
            <Trash2 size={14} />
          </IconButton>
        </div>
      </div>

      {/* Description line */}
      {meta && (
        <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {meta.description}
        </p>
      )}

      {/* Blast-radius warning for destructive types */}
      {meta?.destructive && meta.warning && (
        <EdgeActionWarning warning={meta.warning} />
      )}

      {/* send_webhook: delegate all fields to dedicated component */}
      {action.type === 'send_webhook' && (
        <WebhookFields
          config={action.config}
          showErrors={showErrors}
          onChange={handleConfigChange}
          onPatch={handleConfigPatch}
        />
      )}

      {/* Config fields driven by meta (generic path — not send_webhook) */}
      {action.type !== 'send_webhook' && meta?.fields.map((field) => {
        const value = action.config[field.name] ?? field.default;

        if (field.type === 'channel-select') {
          const missing = showErrors && field.required && !value;
          return (
            <Field
              key={field.name}
              label={field.label}
              required={field.required}
              error={missing ? 'Channel is required.' : undefined}
            >
              {() => (
                <Select
                  value={String(value)}
                  onValueChange={(v) => handleConfigChange(field.name, v)}
                >
                  <SelectTrigger style={{ maxWidth: 360 }} invalid={missing}>
                    <SelectValue placeholder="Select a channel…" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((ch) => (
                      <SelectItem key={ch.id} value={ch.id}>
                        {ch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          );
        }

        if (field.type === 'severity-select') {
          return (
            <Field key={field.name} label={field.label}>
              {() => (
                <Select
                  value={String(value)}
                  onValueChange={(v) => handleConfigChange(field.name, v)}
                >
                  <SelectTrigger style={{ maxWidth: 200 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} style={{ textTransform: 'capitalize' }}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          );
        }

        if (field.type === 'number') {
          const numMissing = showErrors && field.required && (value === '' || value === undefined);
          return (
            <Field
              key={field.name}
              label={field.label}
              required={field.required}
              error={numMissing ? `${field.label} is required.` : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={field.min ?? 0}
                  value={String(value)}
                  onChange={(e) =>
                    handleConfigChange(field.name, Math.max(field.min ?? 0, Number(e.target.value) || 0))
                  }
                  style={{ maxWidth: 160 }}
                  invalid={numMissing}
                />
              )}
            </Field>
          );
        }

        if (field.type === 'watchpoint-multiselect') {
          const ids = Array.isArray(action.config[field.name])
            ? (action.config[field.name] as string[])
            : [];
          return (
            <WatchpointMultiselect
              key={field.name}
              label={field.label}
              selected={ids}
              watchpoints={watchpoints}
              required={field.required}
              showError={showErrors}
              onChange={(next) => handleConfigChange(field.name, next)}
            />
          );
        }

        // text
        const textMissing = showErrors && field.required && !String(value ?? '').trim();
        return (
          <Field
            key={field.name}
            label={field.label}
            required={field.required}
            error={textMissing ? `${field.label} is required.` : undefined}
          >
            {(p) => (
              <Input
                {...p}
                value={String(value)}
                onChange={(e) => handleConfigChange(field.name, e.target.value)}
                style={{ maxWidth: 360 }}
                invalid={textMissing}
              />
            )}
          </Field>
        );
      })}

      {/* channel_id validation guard (redundant safety) */}
      {channelIdMissing && !meta?.fields.find((f) => f.name === 'channel_id') && (
        <p style={{ margin: 0, color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
          Channel is required for Notify On-Call.
        </p>
      )}

      {/* Delay control — only shown between steps (not step 1) */}
      {!isFirst && (
        <Field
          label={`Wait after step ${index} (seconds)`}
          description="How long to wait after the previous action before running this one."
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              value={String(action.delay ?? 0)}
              onChange={(e) => handleDelayChange(e.target.value)}
              style={{ maxWidth: 160 }}
            />
          )}
        </Field>
      )}
    </div>
  );
}
